import { GXPemptyInstrumentsOptions, RUN_STATUS_FIELD, entryType } from "../../../../constants";
import ACTION_LOGS_DATA_MODEL from "../../../../utils/constants/actionLogsDataModel";
import COMMON_LOGS_DATA_MODEL from "../../../../utils/constants/commonLogsDataModel";
import DATA_MODEL_TABLE from "../../../../utils/constants/dataModelTable";
import RUN_LOGS_DATA_MODEL from "../../../../utils/constants/runLogsDataModel";
import { allLogAttributesTable, allLogHierarchyTable } from "../../../../utils/helpers/changeIndex";
import {
  parseTime,
  valueOrEmpty,
  stringOrEmpty,
  replaceEmptyWithHyphen,
  formatUtcToLocal,
  getValueAttributeText,
  isEmptyStatusText,
  validateSelection
} from "../../../../utils/helpers/text";
import { RunStatusLabels } from "../../enums";
import { findItemByKey } from "../../helpers/find-item-by-key";
import { getHierarchyQualificationStatus } from "../../helpers/get-hierarchy-qualification-status";
import ReactDOMServer from "react-dom/server";

const formatRunDateTime = (isoDate, date, time) =>
  isoDate ? formatUtcToLocal(isoDate) : `${valueOrEmpty(date, true)} (${valueOrEmpty(parseTime(time))})`;

const parseSerializedValueAttribute = (value) => {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  if (!normalized.startsWith("{") || !normalized.endsWith("}")) {
    return null;
  }

  const valueMatch = normalized.match(/value\s*=\s*([^,}]*)/i);
  const keyMatch = normalized.match(/key\s*=\s*([^,}]*)/i);
  const parsedValue = valueMatch?.[1]?.trim() || "";
  const parsedKey = keyMatch?.[1]?.trim() || "";

  return parsedValue || parsedKey || "";
};

const getReadableQualificationStatus = (qualificationStatusKey) =>
  validateSelection(GXPemptyInstrumentsOptions.qualificationStatus, {
    key: qualificationStatusKey
  }) || qualificationStatusKey;

const getRunQualificationStatus = (item) => {
  if (item.equipmentDetails?.entryType === entryType.cluster) {
    const clusterStatus = getValueAttributeText(item.gxpReadyCluster);
    return valueOrEmpty(clusterStatus || getReadableQualificationStatus(item.equipmentDetails?.qualificationStatus));
  }
  return valueOrEmpty(
    getValueAttributeText(item.gxpReady) || getReadableQualificationStatus(item.equipmentDetails?.qualificationStatus)
  );
};

const getRunSystemStatus = (item) => {
  if (item.equipmentDetails?.entryType === entryType.cluster) {
    const clusterStatus = getValueAttributeText(item.systemStatusCluster);
    const runLogStatus = getValueAttributeText(item.systemStatus);
    // For cluster entries, prefer cluster status and then fallback to run-log status.
    // Do not fallback to repository status, which can diverge from what was stored in the run log.
    return valueOrEmpty(isEmptyStatusText(clusterStatus) ? runLogStatus : clusterStatus, false, "-");
  }
  return valueOrEmpty(
    getValueAttributeText(item.systemStatus) || item.equipmentDetails?.systemStatus
  );
};

const getSoftwareVersionText = (item) => {
  const rawSoftwareVersion =
    getValueAttributeText(item.instrumentDetails?.softwareVersion) || getValueAttributeText(item.equipmentDetails?.softwareVersion);

  const parsedSoftwareVersion = parseSerializedValueAttribute(rawSoftwareVersion);
  return parsedSoftwareVersion ?? rawSoftwareVersion;
};

/**
 * function that maps the objects fetched from the db to csv file fields we export
 * @param {object} items - unprocessed data
 * @returns data for csv export
 */
export const runLogsItemsToSheet = (items) =>
  items.map((item) => {
    const runStartDateTime = formatRunDateTime(item.runStartDateTime, item.runStartDate, item.runStartTime);
    const runEndDateTime = formatRunDateTime(item.runEndDateTime, item.runEndDate, item.runEndTime);
    return {
      "Equipment ID": valueOrEmpty(item.equipmentId),
      "Run Start": runStartDateTime,
      "Run End": runEndDateTime,
      "Creation date": item.createdAt ? formatUtcToLocal(item.createdAt) : "-",
      "Serial number": valueOrEmpty(item.serialNumber),
      "Material number": valueOrEmpty(item.materialNumber),
      Model: valueOrEmpty(item.equipmentModel),
      "Run status": valueOrEmpty(findItemByKey(RunStatusLabels, item.runStatus)),
      Description: valueOrEmpty(item.description),
      Signee: valueOrEmpty(item.approverUserName),
      Nickname: valueOrEmpty(item.instrumentDetails?.equipmentNickName || item.equipmentDetails?.equipmentNickName),
      "Number of runs": valueOrEmpty(item.numberOfRuns),
      "Number of samples/determinations": valueOrEmpty(item.samplesProcessed),
      "Defect ID": valueOrEmpty(item.defectId),
      Operator: valueOrEmpty(item.operatorUserName),
      "Run ID": valueOrEmpty(item.runIdentification),
      "eLN ID": valueOrEmpty(item.eLNid?.name),
      Assays: item.assay?.join(", "),
      "Media type": stringOrEmpty(item.mediaType, {}),
      Consumables: valueOrEmpty(item.tipsUsed?.value),
      "Qualification status": getRunQualificationStatus(item),
      [DATA_MODEL_TABLE.systemStatus.value]: getRunSystemStatus(item),
      "Intended use GxP relevant": valueOrEmpty(item.equipmentDetails?.gxpRelevant, false, "-"),
      "GxP relevant run": valueOrEmpty(item.gxpRelevantRun, false, "-"),
      Manufacturer: valueOrEmpty(item.instrumentDetails?.manufacturer || item.equipmentDetails?.manufacturer),
      Site: valueOrEmpty(item.site),
      Location: valueOrEmpty(item.instrumentDetails?.location || item.equipmentDetails?.location),
      "Software version": valueOrEmpty(getSoftwareVersionText(item)),
      [DATA_MODEL_TABLE.responsibleProxy.value]: valueOrEmpty(
        item.instrumentDetails?.responsibleProxy || item.equipmentDetails.responsibleProxy
      ),
      [DATA_MODEL_TABLE.responsiblePerson.value]: valueOrEmpty(
        item.instrumentDetails?.responsiblePerson || item.equipmentDetails.responsiblePerson
      )
    };
  });

export const RUN_LOGS_TABLE_HEADERS = [
  { header: "Run start", value: 24 },
  { header: "Run end", value: 24 },
  { header: RUN_LOGS_DATA_MODEL.runStatus.value, value: 18 },
  { header: COMMON_LOGS_DATA_MODEL.description.value, value: 28 },
  { header: COMMON_LOGS_DATA_MODEL.equipmentModel.value, value: 18 },
  { header: "Variant", value: 17 },
  { header: COMMON_LOGS_DATA_MODEL.equipmentId.value, value: 21 },
  { header: COMMON_LOGS_DATA_MODEL.serialNumber.value, value: 20 },
  { header: RUN_LOGS_DATA_MODEL.runIdentification.value, value: 18 },
  { header: COMMON_LOGS_DATA_MODEL.gxpRelevant.value, value: 21 },
  { header: RUN_LOGS_DATA_MODEL.gxpRelevantRun.value, value: 18 },
  { header: ACTION_LOGS_DATA_MODEL.gxpReady.value, value: 21 },
  { header: ACTION_LOGS_DATA_MODEL.systemStatus.value, value: 18 },
  { header: COMMON_LOGS_DATA_MODEL.approverUserName.value, value: 18 }
];

export const RUN_LOGS_CENTRIC_TABLE_HEADERS = [
  { header: "Run start", value: "auto" },
  { header: "Run end", value: "auto" },
  { header: RUN_LOGS_DATA_MODEL.eLNid.value, value: "auto" },
  { header: COMMON_LOGS_DATA_MODEL.description.value, value: 40 },
  { header: RUN_LOGS_DATA_MODEL.runStatus.value, value: "auto" },
  { header: RUN_LOGS_DATA_MODEL.runIdentification.value, value: 20 },
  { header: COMMON_LOGS_DATA_MODEL.gxpRelevant.value, value: "auto" },
  { header: RUN_LOGS_DATA_MODEL.gxpRelevantRun.value, value: "auto" },
  { header: ACTION_LOGS_DATA_MODEL.gxpReady.value, value: 30 },
  { header: ACTION_LOGS_DATA_MODEL.systemStatus.value, value: 20 },
  { header: COMMON_LOGS_DATA_MODEL.approverUserName.value, value: 30 }
];

export const RUN_HIERARCHY_TABLE_HEADERS = [
  { header: "", value: 1 },
  { header: COMMON_LOGS_DATA_MODEL.equipmentModel.value, value: "auto" },
  { header: COMMON_LOGS_DATA_MODEL.location.value, value: "auto" },
  { header: COMMON_LOGS_DATA_MODEL.manufacturer.value, value: "auto" },
  { header: COMMON_LOGS_DATA_MODEL.equipmentId.value, value: "auto" },
  { header: COMMON_LOGS_DATA_MODEL.serialNumber.value, value: "auto" },
  { header: ACTION_LOGS_DATA_MODEL.gxpReady.value, value: "auto" },
  { header: ACTION_LOGS_DATA_MODEL.systemStatus.value, value: "auto" },
  { header: DATA_MODEL_TABLE.gxpRelevant.value + "?", value: "auto" },
  { header: COMMON_LOGS_DATA_MODEL.softwareVersion.value, value: "auto" }
];

export const runLogsItemsToPDF = (items, allLogHierarchyData, logBookEquipment, inventoryId, storeGxpReadys) => {
  let records;
  records = items.map((item) => {
    const runStartDateTime = item.runStartDateTime
      ? formatUtcToLocal(item.runStartDateTime)
      : valueOrEmpty(item.runStartDate, true, "-");
    const runEndDateTime = item.runEndDateTime
      ? formatUtcToLocal(item.runEndDateTime)
      : valueOrEmpty(item.runEndDate, true, "-");
    if (inventoryId) {
      return [
        runStartDateTime,
        runEndDateTime,
        valueOrEmpty(item.eLNid?.name, false, "-"),
        valueOrEmpty(item.description, false, "-"),
        valueOrEmpty(RUN_STATUS_FIELD[item.runStatus], false, "-"),
        valueOrEmpty(item.runIdentification, false, "-"),
        valueOrEmpty(item.equipmentDetails?.gxpRelevant, false, "-"),
        valueOrEmpty(item.gxpRelevantRun, false, "-"),
        item.equipmentDetails?.entryType === entryType.cluster
          ? valueOrEmpty(
              getValueAttributeText(item.gxpReadyCluster) || getReadableQualificationStatus(item.equipmentDetails?.qualificationStatus),
              false,
              "-"
            )
          : valueOrEmpty(getValueAttributeText(item.gxpReady) || getReadableQualificationStatus(item.equipmentDetails?.qualificationStatus), false, "-"),
        item.equipmentDetails?.entryType === entryType.cluster
          ? valueOrEmpty(
              isEmptyStatusText(getValueAttributeText(item.systemStatusCluster))
                ? getValueAttributeText(item.systemStatus)
                : getValueAttributeText(item.systemStatusCluster),
              false,
              "-"
            )
          : valueOrEmpty(getValueAttributeText(item.systemStatus) || item.equipmentDetails?.systemStatus, false, "-"),
        valueOrEmpty(item.approverUserName, false, "-")
      ];
    } else {
      return [
        runStartDateTime,
        runEndDateTime,
        valueOrEmpty(RUN_STATUS_FIELD[item.runStatus], false, "-"),
        valueOrEmpty(item.description, false, "-"),
        valueOrEmpty(item.equipmentModel, false, "-"),
        valueOrEmpty(item.equipmentDetails?.variant, false, "-"),
        valueOrEmpty(item.equipmentId, false, "-"),
        valueOrEmpty(item.serialNumber, false, "-"),
        valueOrEmpty(item.runIdentification, false, "-"),
        valueOrEmpty(item.equipmentDetails?.gxpRelevant, false, "-"),
        valueOrEmpty(item.gxpRelevantRun, false, "-"),
        valueOrEmpty(getValueAttributeText(item.gxpReady), false, "-"),
        valueOrEmpty(getValueAttributeText(item.systemStatus), false, "-"),
        valueOrEmpty(item.approverUserName, false, "-")
      ];
    }
  });
  let hierarchyRecords = null;
  if (
    logBookEquipment?.entryType === entryType.cluster ||
    logBookEquipment?.entryType === entryType?.clusterSubequipment
  ) {
    const allLogItem = allLogHierarchyTable(allLogHierarchyData);
    hierarchyRecords = allLogItem.map((item) => {
      return [
        replaceEmptyWithHyphen(item.clusterParent?.length),
        replaceEmptyWithHyphen(item.equipmentModel),
        replaceEmptyWithHyphen(item.location),
        replaceEmptyWithHyphen(item.manufacturer),
        replaceEmptyWithHyphen(item.equipmentId),
        replaceEmptyWithHyphen(item.serialNumber),
        replaceEmptyWithHyphen(getHierarchyQualificationStatus(item, storeGxpReadys)),
        replaceEmptyWithHyphen(item.systemStatus),
        item.gxpRelevant === "NOTAVAILABLE" ? "-" : replaceEmptyWithHyphen(item.gxpRelevant),
        replaceEmptyWithHyphen(item.softwareVersion)
      ];
    });
  }

  const allLogAttributesItem = allLogAttributesTable(logBookEquipment);
  const attributes = ReactDOMServer.renderToStaticMarkup(allLogAttributesItem);
  const equipmentEntryType =
    logBookEquipment?.entryType === entryType.cluster || logBookEquipment?.entryType === entryType?.clusterSubequipment;

  return {
    headers: inventoryId ? RUN_LOGS_CENTRIC_TABLE_HEADERS : RUN_LOGS_TABLE_HEADERS,
    // hierarchy: hierarchy,
    entryType: equipmentEntryType,
    hierarchyHeaders: RUN_HIERARCHY_TABLE_HEADERS,
    hierarchyRecords: hierarchyRecords,
    attributes: attributes,
    records: records
  };
};
